/**
 * Created by Ning Jiang on 6/8/2016.
 */

import {TweenLite, TimelineLite, Elastic, Linear} from "gsap";
import {TextStyleOptions} from "@nolimitcity/slot-game/bin/text/TextStyleOptions";
import {Oktoberfest} from "../Oktoberfest";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {Layers, OktoberfestLayers} from "./OktoberfestLayers";
import EaseFunction = gsap.EaseFunction;
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {OktoberfestReelView} from "../reels/OktoberfestReelView";
import {OktoberfestSound} from "../sound/OktoberfestSound";
import {GameHelper} from "../../core/utils/GameHelper";
import {Helper} from "@nolimitcity/slot-launcher/bin/utils/Helper";

export class OktoberfestCollectionMeterView extends PIXI.Container {
    static METER_BASE = "meterBase";
    static METER_FILL = "meterFill";
    private static METER_X_POS_OFFSET_PORTRAIT: number = 550;
    private _levelPositions: number[];
    private _fsIconsX: number;
    private _multiIconsX: number;
    private _multiplierAnimationDuration: number = 0;
    private _freespinUpdateDuration: number = 0;
    private _meterAnimationTimeLine: TimelineLite;
    private _meterBase: PIXI.Sprite;
    private _meterFill: PIXI.Sprite;
    private _meterSteps: PIXI.Sprite;
    private _meterContainer: PIXI.Container;
    private _addfsIcons: PIXI.Sprite[];
    private _textIcons: PIXI.Sprite[];
    private _meterIntroAnimation: TimelineSprite;
    private _meterOutroAnimation: TimelineSprite;
    private _introCompleteCallback: Function;
    private _revealTL: TimelineLite;
    private _tokenTotal: number;
    private _tokenWonThisSpin: number;
    private _currentVisualLevel: number = -1;
    private _isPlayingIntro: boolean = false;
    private _introOutroAnimationPosition: PIXI.Point;
    private _addIconStartScale: number = 0.6;
    private _addIconEndScale: number = 1;
    private _meterLayer: PIXI.Container;
    private _multiplierTextIconStyle: TextStyleOptions = {
        fontFamily: "Atreyu",
        fontSize: "32px",
        fill: "#fff264",
        stroke: "#281E0B",
        strokeThickness: 2,
        padding: 10
    };

    constructor() {
        super();
        this._meterLayer = new PIXI.Container();
        this.initAnimations();
        OktoberfestLayers.getLayer(Layers.SCATTER_METER)!.addChild(this);
        this.addChild(this._meterLayer);
        this.onResize();
    }

    private static playMeterDownSound(): void {
        OktoberfestSound.soundPlayer.playTrack({
            name: OktoberfestSound.SOUND_NAME_FS_METER_DOWN
        });
    }

    public onResize(): void {
        if (!(NolimitApplication.isLandscape)) {
            this.onPortrait();
        } else {
            if (!GameHelper.isDesktop()) {
                this._introOutroAnimationPosition = new PIXI.Point(920, 75);
                if (Oktoberfest.isIpad) {
                    this._introOutroAnimationPosition = new PIXI.Point(796, 218);
                }
            } else {
                this._introOutroAnimationPosition = new PIXI.Point(1057, 43);
            }
            this.onLandScape();
            if (!GameHelper.isDesktop()) {
                this._meterContainer.position.x = 920;
                if (Oktoberfest.isIpad) {
                    this._meterContainer.position.x = 796;
                }
            }
        }
    }

    public onAddToStage(data: { playIntro: boolean, completeCallback?: Function }): void {
        this._currentVisualLevel = -1;
        if (data.playIntro) {
            this._isPlayingIntro = true;
            this._meterContainer.visible = false;
            this._meterIntroAnimation.visible = true;
            this._meterIntroAnimation.alpha = 1;
            this._meterOutroAnimation.alpha = 0;
            this.playIntro(data.completeCallback!);
        } else {

            if (this._isPlayingIntro) {
                if (this._meterIntroAnimation) {
                    this._meterIntroAnimation.hide();
                }
                if (this._revealTL.isActive()) {
                    this._revealTL.pause();
                }
                this._isPlayingIntro = false;
            }
            this._meterIntroAnimation.visible = false;
            this._meterIntroAnimation.alpha = 1;
            this._meterOutroAnimation.alpha = 0;
            this._meterContainer.visible = true;
            this.updateLevelAnimation({shouldAnimate: false});
        }
    }

    public onRemoveFromStage(): void {
        this._meterContainer.visible = false;
        this.resetIcons();
    }

    public onMultiplierAnimationCreated(duration: number): void {
        this._multiplierAnimationDuration = duration;
    }

    public onFreespinLeftAnimationCreated(duration: number): void {
        this._freespinUpdateDuration = duration;
    }

    // Since the keypad doesn't allowed to abort the wp in freespin, we don't take care of abort here for now.
    public updateLevelAnimation(data: { shouldAnimate: boolean, timeLine?: TimelineLite, completeCallback?: Function }): void {
        if (Oktoberfest.model.isRestore) {
            this._tokenTotal = Oktoberfest.model.parsedInitData.beerTokensCollectedInFreespin;
            this._tokenWonThisSpin = this._tokenTotal;
            this._currentVisualLevel = this._tokenTotal;
        } else {

            this._tokenWonThisSpin = Oktoberfest.model.parsedGameData.beerTokensCollectedThisSpin;
            this._tokenTotal = Oktoberfest.model.parsedGameData.beerTokensCollectedInFreespin;
        }

        if (this._meterAnimationTimeLine != null && this._meterAnimationTimeLine.isActive()) {
            this._meterAnimationTimeLine.kill();
        }

        let timePerStep: number = data.shouldAnimate ? 0.27 : 0;
        let multiWinningIconAnimationStartTime: number = 0;
        this._meterAnimationTimeLine = new TimelineLite({
            onComplete: () => {
            }
        });
        if (data.timeLine) {
            data.timeLine.add(this._meterAnimationTimeLine);
        }

        //The meter fill animation
        let numberOfNewSteps: number = Math.min(this._tokenWonThisSpin, this._tokenTotal - this._currentVisualLevel);
        if (data.shouldAnimate) {
            for (let i: number = 1; i <= numberOfNewSteps; i++) {
                let nextStepIndex: number = this._currentVisualLevel + i;
                let isNewLevelStep: boolean = nextStepIndex % 3 === 0;
                let soundName: string = OktoberfestSound.SOUND_NAME_FS_METER_UP;
                if (isNewLevelStep) {
                    soundName = OktoberfestSound.SOUND_NAME_FS_METER_UP_NEW_LEVEL
                }
                this._meterAnimationTimeLine.add([
                    new TweenLite(this._meterFill, 0.24, {y: this._levelPositions[nextStepIndex]}), //Duration match
                    () => {
                        OktoberfestSound.playSoundEffect(soundName);
                    }
                ], timePerStep * i);
            }
            multiWinningIconAnimationStartTime = this._meterAnimationTimeLine.totalDuration();
        } else {
            this._meterAnimationTimeLine.add(
                new TweenLite(this._meterFill, timePerStep * this._tokenWonThisSpin, {
                    y: this._levelPositions[this._tokenTotal],
                    ease: Linear.easeNone
                })
            );
        }

        let iconsPassedTimings: [number, number][] = [];
        for (let i: number = 1; i <= numberOfNewSteps; i++) {
            let currentValue: number = this._currentVisualLevel + i;
            if (currentValue % 3 === 0) {
                iconsPassedTimings.push([i, currentValue / 3 - 1]);
            }
        }

        let firstAnimatedIconIndex: number = iconsPassedTimings.length > 0 ? iconsPassedTimings[0][1] : Math.floor(this._tokenTotal / 3);
        for (let i: number = 0; i < this._addfsIcons.length; i++) {
            this._addfsIcons[i].alpha = i < firstAnimatedIconIndex ? 0 : 1;
            this._textIcons[i].scale.set(i < firstAnimatedIconIndex ? 0 : 1, i < firstAnimatedIconIndex ? 0 : 1);
        }

        if (iconsPassedTimings.length > 0) {
            if (data.shouldAnimate) {
                // winning icons attention.
                for (let i: number = 0; i < iconsPassedTimings.length; i++) {
                    let stepTiming: number = iconsPassedTimings[i][0];
                    let iconIndex: number = iconsPassedTimings[i][1];
                    // Do not play the multi icon attention if it on the current level, play winning animation directly.
                    if (stepTiming < numberOfNewSteps) {
                        this._meterAnimationTimeLine.add(
                            new TweenLite(this._textIcons[iconIndex].scale, 0.2, {
                                x: 1.2,
                                y: 1.2,
                                ease: Elastic.easeOut.config(1.1, 0.4),
                                delay: 0.16
                            }),
                            timePerStep * stepTiming
                        );
                        multiWinningIconAnimationStartTime = Math.max(multiWinningIconAnimationStartTime, this._meterAnimationTimeLine.totalDuration());
                    }
                    this._meterAnimationTimeLine.add(
                        new TweenLite(this._addfsIcons[iconIndex].scale, 0.2, {
                            x: 1.2,
                            y: 1.2,
                            ease: Elastic.easeOut.config(1.1, 0.4),
                            delay: 0.16
                        }),
                        timePerStep * stepTiming
                    );
                }

                // Icons winning animation.
                let fsWinningIconAnimationStartTime: number = multiWinningIconAnimationStartTime + this._multiplierAnimationDuration;
                let glassAnimationStartTime: number = fsWinningIconAnimationStartTime + 0.4;
                for (let i: number = 0; i < iconsPassedTimings.length; i++) {
                    let iconIndex = iconsPassedTimings[i][1];

                    // Multiplier icon
                    this._meterAnimationTimeLine.add([
                        new TweenLite(this._textIcons[iconIndex], 1.5, {y: this._textIcons[iconIndex].y - 75}),
                        new TweenLite(this._textIcons[iconIndex].scale, 0.2, {x: 2, y: 2}),
                        new TweenLite(this._textIcons[iconIndex], 1, {alpha: 0, delay: 0.5}),
                    ], multiWinningIconAnimationStartTime);

                    // +fs icon
                    this._meterAnimationTimeLine.add([
                        new TweenLite(this._addfsIcons[iconIndex].scale, 0.7, {
                            x: this._addIconEndScale,
                            y: this._addIconEndScale
                        }),
                        new TweenLite(this._addfsIcons[iconIndex], 0.7, {alpha: 0}),
                    ], fsWinningIconAnimationStartTime);
                }

                // Add label for multiplier to play
                data!.timeLine!.add("multiplier", multiWinningIconAnimationStartTime); // +offset?

                // Add label for freespinLeftNumber to play
                data.timeLine!.add("freespinsLeft", fsWinningIconAnimationStartTime); // +offset?

                data.timeLine!.add("bierglass", glassAnimationStartTime); // +offset?

                // Add an empty tween to make the timeLine wait for the freespinLeft label to finish.
                this._meterAnimationTimeLine.add(new TweenLite(this, 0.001, {}), glassAnimationStartTime + this._freespinUpdateDuration);
            } else {
                for (let i: number = 0; i < iconsPassedTimings.length; i++) {
                    let iconIndex: number = iconsPassedTimings[i][1];
                    this._meterAnimationTimeLine.add([
                        new TweenLite(this._addfsIcons[iconIndex], timePerStep, {alpha: 0}),
                        new TweenLite(this._textIcons[iconIndex].scale, timePerStep, {x: 0, y: 0})
                    ], 0);
                }
            }
        }

        //If meter is full, remove it.
        if (this._tokenTotal >= 12) {
            if (data.shouldAnimate) {
                this._meterAnimationTimeLine.add([
                    () => this.playMaxedOutAnimation(),
                    new TweenLite(this._meterOutroAnimation, 0.001, {alpha: 1}),
                    new TweenLite(this._meterIntroAnimation, 0.002, {alpha: 0}),
                ]);

                this._meterAnimationTimeLine.add([
                    new TweenLite(this._meterIntroAnimation, 0.001, {alpha: 1}),
                    new TweenLite(this._meterOutroAnimation, 0.001, {alpha: 0}),
                ], this._meterAnimationTimeLine.totalDuration());//+ AnimationHelper.getDurationFromFrames(this._meterOutroAnimation.totalFrames, 30
            } else {
                this._meterContainer.visible = false;
            }
        }

        this._currentVisualLevel = this._tokenTotal;
    }

    protected onPortrait(): void {
        const isIpad: boolean = Oktoberfest.isIpad,
            isIpadPortrait: boolean = isIpad && !(NolimitApplication.isLandscape),
            isIpadLandscape: boolean = isIpad && NolimitApplication.isLandscape;

        const scale: number = isIpadPortrait ? 0.85 : 0.95;// NolimitApplication.screenBounds.scale +0.2;

        this._meterContainer.position.x = OktoberfestReelView.REEL_AREAS[0].x + (OktoberfestCollectionMeterView.METER_X_POS_OFFSET_PORTRAIT * scale);

        //Since the meter is rotated the width/height feels a bit messed up. :)
        this._meterContainer.rotation = 90 / 180 * Math.PI;
        this._meterContainer.scale.set(scale, scale);
        this._meterContainer.position.y = isIpadLandscape ? 60 : -36;
        this._meterContainer.position.x -= 10;

        this._meterIntroAnimation.scale.set(scale, scale);
        this._meterIntroAnimation.position = this._meterContainer.position;
        this._meterIntroAnimation.position.y += (0.5 * scale);
        this._meterIntroAnimation.position.x += (10.5 * scale);
        this._meterIntroAnimation.rotation = this._meterContainer.rotation;

        this._meterOutroAnimation.scale.set(scale, scale);
        this._meterOutroAnimation.position = this._meterContainer.position;
        this._meterOutroAnimation.position.y += (0.5 * scale);
        this._meterOutroAnimation.position.x += (10.5 * scale);
        this._meterOutroAnimation.rotation = this._meterContainer.rotation;

        for (let i: number = 0; i < this._addfsIcons.length; i++) {
            this._addfsIcons[i].rotation = 270 / 180 * Math.PI;
        }
        for (let j: number = 0; j < this._textIcons.length; j++) {
            this._textIcons[j].rotation = 270 / 180 * Math.PI;
        }
    }

    protected onLandScape(): void {
        const isIpad: boolean = Oktoberfest.isIpad,
            scale: number = isIpad ? 0.65 : 1;

        if (isIpad) {
            this._meterContainer.position.set(796, 225);
        } else {
            this._meterContainer.position.set(720, 86);
        }
        //Since the meter is rotated the width/height feels a bit messed up. :)
        this._meterContainer.rotation = 0;
        this._meterIntroAnimation.rotation = 0;
        this._meterOutroAnimation.rotation = 0;
        this._meterContainer.scale.set(scale, scale);
        this._meterIntroAnimation.scale.set(scale, scale);
        this._meterOutroAnimation.scale.set(scale, scale);

        for (let i: number = 0; i < this._addfsIcons.length; i++) {
            this._addfsIcons[i].rotation = 0
        }
        for (let j: number = 0; j < this._textIcons.length; j++) {
            this._textIcons[j].rotation = 0;
        }
        this._meterIntroAnimation.anchor.set(0.5, 0);
        this._meterOutroAnimation.anchor.set(0.5, 0);
        this._meterIntroAnimation.position.set(this._introOutroAnimationPosition.x, this._introOutroAnimationPosition.y);
        this._meterOutroAnimation.position.set(this._introOutroAnimationPosition.x, this._introOutroAnimationPosition.y);
    }

    private initAnimations(): void {
        this._tokenWonThisSpin = 0;
        this._tokenTotal = Oktoberfest.model.parsedInitData.beerTokensCollectedInFreespin;
        this._levelPositions = [
            488,
            451,
            410,
            370,
            329,
            288,
            248,
            206,
            167,
            125,
            84,
            44,
            6
        ];

        this._fsIconsX = 39;
        this._multiIconsX = -43;
        this._introOutroAnimationPosition = new PIXI.Point(1057, 43);

        this._addIconStartScale *= 1.5;
        this._addIconEndScale *= 1.5;

        this.createMeter();
        this.createMeterIntroMovieClip();
        this.createMeterOutroMovieClip();
        this.createRevealTimeLine();

    }

    private createMeter(): void {
        let offsetX: number = 3.5;
        this._meterContainer = new PIXI.Container();
        //Base
        this._meterBase = new PIXI.Sprite(Oktoberfest.assetLoader.getTextures(OktoberfestCollectionMeterView.METER_BASE)[0]);
        this._meterBase.anchor.set(0.5, 0);
        this._meterContainer.addChild(this._meterBase);

        //Fill
        this._meterFill = new PIXI.Sprite(Oktoberfest.assetLoader.getTextures(OktoberfestCollectionMeterView.METER_FILL)[0]);
        this._meterFill.position.set(offsetX, this._levelPositions[this._levelPositions.length - 1]);
        this._meterFill.anchor.set(0.5, 0);
        this._meterContainer.addChild(this._meterFill);

        //mask
        let maskShape: PIXI.Graphics = new PIXI.Graphics();
        maskShape.beginFill(0xFFFFFF, 1);
        maskShape.drawRect(-2, 6, 12, 477);
        maskShape.endFill();
        this._meterContainer.addChild(maskShape);
        this._meterContainer.position.set(720, 86);
        this._meterFill.mask = maskShape;

        //Steps (foreground)
        this._meterSteps = new PIXI.Sprite(Oktoberfest.assetLoader.getTextures("meterSteps")[0]);
        this._meterSteps.anchor.set(0.5, 0);
        this._meterContainer.addChild(this._meterSteps);

        // add three FS icons and texts
        this._addfsIcons = [];
        this._textIcons = [];
        for (let i: number = 0; i < 4; i++) {
            let level: number = (i + 1) * 3;
            let multiplier: string = "x" + (i + 2);
            //Icons
            let addThreeFSIcon: PIXI.Sprite = new PIXI.Sprite(Oktoberfest.assetLoader.getTextures("addThreeFsIcon")[0]);
            addThreeFSIcon.anchor.set(0.5, 0.5);
            addThreeFSIcon.position.set(this._fsIconsX, this._levelPositions[level]);
            addThreeFSIcon.scale.set(this._addIconStartScale, this._addIconStartScale);

            this._addfsIcons.push(addThreeFSIcon);
            this._meterContainer.addChild(addThreeFSIcon);

            //Texts
            let textIcon: PIXI.Text = new PIXI.Text(multiplier, this._multiplierTextIconStyle);
            textIcon.anchor.set(0.5, 0.5);
            textIcon.position.set(this._multiIconsX, this._levelPositions[level]);
            this._textIcons.push(textIcon);
            this._meterContainer.addChild(textIcon);
        }
        this._meterContainer.visible = false;
        this._meterLayer.addChild(this._meterContainer);
    }

    private createMeterIntroMovieClip(): void {
        this._meterIntroAnimation = new TimelineSprite(Oktoberfest.assetLoader.getTextures("meterStartAnimation"));
        this._meterIntroAnimation.position.set(this._introOutroAnimationPosition.x, this._introOutroAnimationPosition.y);
        this._meterIntroAnimation.anchor.set(0.5, 0);
        this._meterIntroAnimation.visible = false;
        this._meterLayer.addChild(this._meterIntroAnimation);
    }

    private createMeterOutroMovieClip(): void {
        this._meterOutroAnimation = new TimelineSprite(Oktoberfest.assetLoader.getTextures("meterStartAnimation"));
        this._meterOutroAnimation.position.set(this._introOutroAnimationPosition.x, this._introOutroAnimationPosition.y);
        this._meterOutroAnimation.anchor.set(0.5, 0);
        this._meterOutroAnimation.visible = false;
        this._meterLayer.addChild(this._meterOutroAnimation);
    }

    private createRevealTimeLine(): void {
        this._revealTL = new TimelineLite({
            onComplete: this.onMeterLevelRevealAnimationComplete.bind(this),
            paused: true
        });
        let fillDuration: number = 0.2;
        let iconDuration: number = 0.3;
        let iconEase: EaseFunction = Elastic.easeOut.config(1.1, 0.4);

        this._revealTL.add([
            new TweenLite(this._addfsIcons[3], iconDuration, {alpha: 1}),
            new TweenLite(this._textIcons[3].scale, iconDuration, {x: 1, y: 1, ease: iconEase}),
        ]);

        this._revealTL.add([
            () => OktoberfestCollectionMeterView.playMeterDownSound(),
            new TweenLite(this._meterFill, fillDuration, {y: this._levelPositions[9]})]
        );

        this._revealTL.add([
            new TweenLite(this._addfsIcons[2], iconDuration, {alpha: 1}),
            new TweenLite(this._textIcons[2].scale, iconDuration, {x: 1, y: 1, ease: iconEase}),
        ]);

        this._revealTL.add([
            () => OktoberfestCollectionMeterView.playMeterDownSound(),
            new TweenLite(this._meterFill, fillDuration, {y: this._levelPositions[6]})
        ]);

        this._revealTL.add([
            new TweenLite(this._addfsIcons[1], iconDuration, {alpha: 1}),
            new TweenLite(this._textIcons[1].scale, iconDuration, {x: 1, y: 1, ease: iconEase}),
        ]);

        this._revealTL.add([
            () => OktoberfestCollectionMeterView.playMeterDownSound(),
            new TweenLite(this._meterFill, fillDuration, {y: this._levelPositions[3]})
        ]);

        this._revealTL.add([
            new TweenLite(this._addfsIcons[0], iconDuration, {alpha: 1}),
            new TweenLite(this._textIcons[0].scale, iconDuration, {x: 1, y: 1, ease: iconEase}),
        ]);

        this._revealTL.add([
            () => OktoberfestCollectionMeterView.playMeterDownSound(),
            new TweenLite(this._meterFill, fillDuration, {y: this._levelPositions[0]})
        ]);
    }

    private playMaxedOutAnimation(): void {
        this._meterOutroAnimation.visible = true;
        this._meterOutroAnimation.getAnimation([0]).reverse();
        this._meterContainer.visible = false;
    }

    private onMeterLevelRevealAnimationComplete(): void {
        this._isPlayingIntro = false;
        this._introCompleteCallback();
    }

    private playIntro(callback: Function): void {
        this.prepareGraphicsForIntro();
        this._introCompleteCallback = callback;
        OktoberfestSound.soundPlayer.playTrack({
            name: OktoberfestSound.SOUND_NAME_FS_METER_ENTERS
        });
        this._meterIntroAnimation.getAnimation([0], () => {
        }, () => this.meterIntroAnimationComplete());
    }

    private prepareGraphicsForIntro(): void {
        this._revealTL.pause(0, true);
        this._meterFill.position.set(4, this._levelPositions[this._levelPositions.length - 1]);

        for (var i: number = 0; i < this._addfsIcons.length; i++) {
            this._addfsIcons[i].alpha = 0;
            this._textIcons[i].scale.set(0, 0);
        }
    }

    private meterIntroAnimationComplete(): void {
        this._meterIntroAnimation.visible = false;
        this._meterContainer.visible = true;
        this._revealTL.play();
    }

    private resetIcons(): void {
        for (let i: number = 0; i < 4; i++) {
            let level: number = (i + 1) * 3;
            this._addfsIcons[i].position.set(this._fsIconsX, this._levelPositions[level]);
            this._addfsIcons[i].alpha = 1;
            this._addfsIcons[i].scale.set(this._addIconStartScale, this._addIconStartScale);
            this._textIcons[i].position.set(this._multiIconsX, this._levelPositions[level]);
            this._textIcons[i].alpha = 1;
            this._textIcons[i].scale.set(1, 1);
        }
    }
}

